/**
 * MapComponent - Real-time vehicle tracking and visualization on Google Maps.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  MarkerClusterer,
  DirectionsRenderer,
} from '@react-google-maps/api'
import moment from 'moment'
import {useSelector} from 'react-redux'
import {smoothTransition} from '../../../utils/SmoothTransition'

export type MapRouteLocation = {
  lat: number
  lng: number
  name?: string
}

const options = {
  zoomControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
  maxZoom: 20,
  minZoom: 3,
}

const clusterOptions = {
  imagePath:
    'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
  maxZoom: 15,
  minimumClusterSize: 5,
  averageCenter: true,
}

export type MapInfoVehicle = {
  requestId?: string | null
  jobStatus?: string | null
  vehicleNumber?: string | null
  vehicleType?: string | null
  baseLocation?: string | null
}

type Props = {
  selectedFocusId: number
  selectedMarkerId: number
  setSelectedMarkerId: (id: number) => void
  routeLocations?: MapRouteLocation[]
  mapContainerHeight?: string
  mapFullBleed?: boolean
  followVehicleId?: number
  showLegend?: boolean
  showMapToolbar?: boolean
  labelMode?: 'always' | 'zoomOrSelected'
  fitAllToken?: number
  onClearSelection?: () => void
  onToggleFollow?: () => void
  onMarkerSelect?: (id: number) => void
  /** Select all current list vehicles onto the map (empty-state CTA) */
  onShowAll?: () => void
  /** Optional job context for enriched Monitoring InfoWindow */
  infoVehicle?: MapInfoVehicle | null
}

const arrow =
  'M21.9,26.42L16,22.04l-5.9,4.38c-0.09,0.07-0.21-0.02-0.18-0.12l6-19.88c0.02-0.07,0.13-0.07,0.15,0l6,19.88C22.1,26.4,21.98,26.48,21.9,26.42 Z'

const box = 'M5,5 L15,5 L15,15 L5,15 Z'

const MapComponent: React.FC<Props> = ({
  selectedFocusId,
  selectedMarkerId,
  setSelectedMarkerId,
  routeLocations,
  mapContainerHeight = '82vh',
  mapFullBleed = false,
  followVehicleId = 0,
  showLegend = false,
  showMapToolbar = false,
  labelMode = 'always',
  fitAllToken = 0,
  onClearSelection,
  onToggleFollow,
  onMarkerSelect,
  onShowAll,
  infoVehicle = null,
}) => {
  const initialCenter = {
    lat: 6.972886710924382,
    lng: 79.91722984353211,
  }
  const {GPSVehiclesDetails, MapViewVehicle} = useSelector((state: any) => {
    return {
      GPSVehiclesDetails: state.MapData?.GPSVehiclesDetails || [],
      MapViewVehicle: state.MapData?.MapViewVehicle || [],
    }
  })
  const [center, setCenter] = useState(initialCenter)
  const [markers, setMarkers] = useState(GPSVehiclesDetails)
  const [google, setGoogle] = useState<any>(window.google ? window.google : {})
  const [map, setMap] = useState<any>(null)
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)
  const [zoom, setZoom] = useState(10)
  const routeBoundsFitted = useRef(false)
  const [selectedRouteIndex, setSelectedRouteIndex] = useState<number | null>(null)
  const [directions, setDirections] = useState<any>(null)
  const lastFitToken = useRef(0)

  const focusedSuccessfullyId = useRef<number>(0)

  useEffect(() => {
    if (!selectedFocusId || !map) {
      focusedSuccessfullyId.current = 0
      return
    }
    if (focusedSuccessfullyId.current === selectedFocusId) return
    const selectedMarker = markers.find((item: any) => item.id === selectedFocusId)
    if (!selectedMarker?.pos?.x || !selectedMarker?.pos?.y) return
    const newCenter = {lat: selectedMarker.pos.y, lng: selectedMarker.pos.x}
    setCenter(newCenter)
    setZoom(15)
    map.panTo(newCenter)
    map.setZoom(15)
    focusedSuccessfullyId.current = selectedFocusId
  }, [selectedFocusId, map, markers])

  useEffect(() => {
    if (!followVehicleId || !map) return
    const followed = markers.find((item: any) => item.id === followVehicleId)
    if (!followed?.pos?.x || !followed?.pos?.y) return
    const newCenter = {lat: followed.pos.y, lng: followed.pos.x}
    setCenter(newCenter)
    map.panTo(newCenter)
  }, [followVehicleId, markers, map])

  useEffect(() => {
    if (!map || !isGoogleLoaded || !fitAllToken || fitAllToken === lastFitToken.current) return
    lastFitToken.current = fitAllToken
    const valid = markers.filter((item: any) => item?.pos?.x && item?.pos?.y)
    if (valid.length === 0) return
    const bounds = new google.maps.LatLngBounds()
    valid.forEach((item: any) => bounds.extend({lat: item.pos.y, lng: item.pos.x}))
    map.fitBounds(bounds, 64)
  }, [fitAllToken, map, isGoogleLoaded, markers, google])

  useEffect(() => {
    if (!isGoogleLoaded || !routeLocations || routeLocations.length < 2) return

    const origin = routeLocations[0]
    const destination = routeLocations[routeLocations.length - 1]

    const waypoints = routeLocations.slice(1, -1).map((p) => ({
      location: {lat: p.lat, lng: p.lng},
      stopover: true,
    }))

    const service = new google.maps.DirectionsService()

    service.route(
      {
        origin: {lat: origin.lat, lng: origin.lng},
        destination: {lat: destination.lat, lng: destination.lng},
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (
        result: google.maps.DirectionsResult | null,
        status: google.maps.DirectionsStatus
      ) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          setDirections(result)
        } else {
          console.error('Directions request failed:', status)
        }
      }
    )
  }, [routeLocations, isGoogleLoaded, google])

  const onDragEnd = useCallback((mapInstance: any) => {
    if (!mapInstance) return
    const newCenter = {
      lat: mapInstance.getCenter().lat(),
      lng: mapInstance.getCenter().lng(),
    }
    setCenter(newCenter)
  }, [])

  const getIcon = useCallback(
    (item: any) => {
      return {
        path: item?.pos?.s === 0 ? box : arrow,
        scale: 1.3,
        strokeColor: 'black',
        strokeWeight: 1.2,
        fillOpacity: 1,
        fillColor: item?.pos?.s === 0 ? '#fd0017' : item?.pos?.s > 0 ? '#00ff00' : '#404040',
        offset: '5%',
        anchor: isGoogleLoaded ? new google.maps.Point(15, 30) : undefined,
        rotation: item?.pos?.s === 0 ? 0 : item?.pos?.c ? item?.pos?.c : 0,
      }
    },
    [google, isGoogleLoaded]
  )

  const onLoad = useCallback((mapInstance: any) => {
    setGoogle(window.google)
    setIsGoogleLoaded(true)
    setMap(mapInstance)
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  const googleMapsApiKey: string = process.env.REACT_APP_GOOGLE_PLACES_KEY || ''

  const {isLoaded} = useJsApiLoader({
    googleMapsApiKey,
    id: 'google-map-script',
  })

  const mapStyles = useMemo(
    () => ({
      height: mapContainerHeight,
      width: '100%',
    }),
    [mapContainerHeight]
  )

  useEffect(() => {
    if (!map || !isGoogleLoaded || !routeLocations?.length || routeBoundsFitted.current) {
      return
    }
    const bounds = new google.maps.LatLngBounds()
    routeLocations.forEach((p) => bounds.extend({lat: p.lat, lng: p.lng}))
    map.fitBounds(bounds, 64)
    routeBoundsFitted.current = true
  }, [map, isGoogleLoaded, routeLocations, google])

  const shouldShowLabel = useCallback(
    (itemId: number) => {
      if (labelMode === 'always') return true
      return zoom >= 14 || selectedMarkerId === itemId || selectedFocusId === itemId
    },
    [labelMode, zoom, selectedMarkerId, selectedFocusId]
  )

  const GoogleMapMarkers = useMemo(() => {
    if (markers?.length > 0) {
      const validMarkers = markers.filter((item: any) => item?.pos?.x && item?.pos?.y)
      return (
        <MarkerClusterer options={clusterOptions}>
          {(clusterer) => (
            <>
              {validMarkers.map((item: any) => (
                <Marker
                  key={item.id}
                  position={{lng: item.pos.x, lat: item.pos.y}}
                  icon={getIcon(item)}
                  label={
                    shouldShowLabel(item.id)
                      ? {
                          text: item?.nm || ' ',
                          color: '#fd0017',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }
                      : undefined
                  }
                  onClick={() => {
                    setSelectedMarkerId(item?.id)
                    onMarkerSelect?.(item?.id)
                  }}
                  clusterer={clusterer}
                />
              ))}
            </>
          )}
        </MarkerClusterer>
      )
    }
    return null
  }, [
    getIcon,
    markers,
    setSelectedMarkerId,
    shouldShowLabel,
    onMarkerSelect,
  ])

  useEffect(() => {
    if (MapViewVehicle?.length > 0) {
      const filteredGPSVehiclesDetails = GPSVehiclesDetails.filter((vehicle: any) =>
        MapViewVehicle.includes(vehicle.id)
      )

      const currentIds = new Set(filteredGPSVehiclesDetails.map((marker: {id: any}) => marker.id))
      const currentMarkers = markers || []
      const remainingMarkers = currentMarkers.filter((marker: {id: unknown}) =>
        currentIds.has(marker.id)
      )

      const updatedMarkers = filteredGPSVehiclesDetails.map((newMarker: any) => {
        const existingMarker = remainingMarkers.find(
          (marker: {id: any}) => marker.id === newMarker.id
        )
        if (existingMarker) {
          if (
            existingMarker.pos?.x !== newMarker.pos?.x ||
            existingMarker.pos?.y !== newMarker.pos?.y
          ) {
            if (existingMarker.cleanup) {
              existingMarker.cleanup()
            }

            if (
              existingMarker.pos?.x &&
              existingMarker.pos?.y &&
              newMarker.pos?.x &&
              newMarker.pos?.y
            ) {
              const updatedMarker = {
                ...existingMarker,
                pos: {...existingMarker.pos},
                targetPos: {...newMarker.pos},
              }

              updatedMarker.cleanup = smoothTransition(
                existingMarker.pos,
                newMarker.pos,
                (newPos: any) => {
                  setMarkers((prevMarkers: any[]) =>
                    prevMarkers.map((marker: {id: any}) =>
                      marker.id === newMarker.id
                        ? {
                            ...marker,
                            pos: {
                              x: newPos.lng,
                              y: newPos.lat,
                              s: newMarker.pos.s,
                              c: newMarker.pos.c,
                              t: newMarker.pos.t,
                            },
                          }
                        : marker
                    )
                  )
                }
              )

              return updatedMarker
            }
          }

          return {
            ...existingMarker,
            pos: newMarker.pos,
            nm: newMarker.nm,
            lmsg: newMarker.lmsg,
          }
        }
        return newMarker
      })

      setMarkers(updatedMarkers)
    } else {
      setMarkers([])
    }

    return () => {
      if (markers?.length > 0) {
        markers.forEach((marker: any) => {
          if (marker.cleanup) {
            marker.cleanup()
          }
        })
      }
    }
    // Intentionally omit `markers` from deps to avoid animation reset loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GPSVehiclesDetails, MapViewVehicle])

  const selectedMarker = markers?.find((item: any) => item.id === selectedMarkerId)
  const hasVisibleMarkers = markers?.some((item: any) => item?.pos?.x && item?.pos?.y)

  return (
    <div
      className={
        mapFullBleed
          ? 'w-100 h-100 position-relative'
          : 'col-sm-12 col-md-12 col-lg-12 position-relative h-100'
      }
    >
      <div
        className={
          mapFullBleed
            ? 'd-flex h-100 w-100 justify-content-center align-items-stretch position-relative'
            : 'bg-light-primary d-flex h-100 d-flex justify-content-center align-items-center rounded min-h-175px position-relative'
        }
      >
        {isLoaded && (
          <GoogleMap
            center={center}
            zoom={zoom}
            mapContainerStyle={mapStyles}
            options={options}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onDragEnd={() => onDragEnd(map)}
            onZoomChanged={() => {
              if (map) setZoom(map.getZoom())
            }}
          >
            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#1d4ed8',
                    strokeWeight: 4,
                    strokeOpacity: 0.9,
                  },
                }}
              />
            )}

            {isGoogleLoaded &&
              routeLocations?.map((p, i) => {
                const isFirst = i === 0
                const isLast = i === routeLocations.length - 1
                const label = isFirst ? 'P' : isLast ? 'D' : String(i)

                return (
                  <Marker
                    key={`route-loc-${i}-${p.lat}-${p.lng}`}
                    position={{lat: p.lat, lng: p.lng}}
                    title={p.name || `Stop ${i + 1}`}
                    onClick={() => setSelectedRouteIndex(i)}
                    label={{
                      text: label,
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 'bold',
                    }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 12,
                      fillColor: isFirst ? '#22c55e' : isLast ? '#ef4444' : '#1d4ed8',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                    }}
                  />
                )
              })}

            {selectedRouteIndex !== null && routeLocations?.[selectedRouteIndex] && (
              <InfoWindow
                position={{
                  lat: routeLocations[selectedRouteIndex].lat,
                  lng: routeLocations[selectedRouteIndex].lng,
                }}
                onCloseClick={() => setSelectedRouteIndex(null)}
              >
                <div style={{padding: '8px 10px', maxWidth: '220px'}}>
                  <div style={{fontWeight: 'bold', marginBottom: '4px'}}>
                    {selectedRouteIndex === 0
                      ? 'Pickup Location'
                      : selectedRouteIndex === routeLocations.length - 1
                        ? 'Delivery Location'
                        : `Stop ${selectedRouteIndex}`}
                  </div>
                  <div style={{fontSize: '13px', color: '#333'}}>
                    {routeLocations[selectedRouteIndex].name || 'Unnamed location'}
                  </div>
                </div>
              </InfoWindow>
            )}

            {isGoogleLoaded && GoogleMapMarkers}
            {selectedMarker && (
              <InfoWindow
                position={{
                  lng: selectedMarker.pos.x,
                  lat: selectedMarker.pos.y,
                }}
                onCloseClick={() => setSelectedMarkerId(0)}
              >
                <div className='vm-infowindow'>
                  {infoVehicle ? (
                    <>
                      {infoVehicle.requestId !== undefined || infoVehicle.jobStatus != null ? (
                        <>
                          <div className='vm-infowindow__header'>
                            <div className='vm-infowindow__header-main'>
                              <div className='vm-infowindow__title-row'>
                                <div className='vm-infowindow__title'>
                                  {infoVehicle.vehicleNumber || selectedMarker.nm}
                                </div>
                                {infoVehicle.jobStatus ? (
                                  <span className='vm-infowindow__job-status'>
                                    {infoVehicle.jobStatus}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-light vm-infowindow__close'
                              aria-label='Close'
                              onClick={() => setSelectedMarkerId(0)}
                            >
                              <i className='bi bi-x-lg'></i>
                            </button>
                          </div>
                          <div className='vm-infowindow__subtitle'>
                            Request ID {infoVehicle.requestId || '—'}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='vm-infowindow__header'>
                            <div className='vm-infowindow__header-main'>
                              <div className='vm-infowindow__title'>
                                {infoVehicle.vehicleNumber || selectedMarker.nm}
                              </div>
                            </div>
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-light vm-infowindow__close'
                              aria-label='Close'
                              onClick={() => setSelectedMarkerId(0)}
                            >
                              <i className='bi bi-x-lg'></i>
                            </button>
                          </div>
                          {infoVehicle.vehicleType ? (
                            <div className='vm-infowindow__subtitle'>{infoVehicle.vehicleType}</div>
                          ) : null}
                        </>
                      )}
                    </>
                  ) : (
                    <div className='vm-infowindow__title'>{selectedMarker.nm}</div>
                  )}
                  <div className='vm-infowindow__gps'>
                    {selectedMarker.pos?.s > 0
                      ? 'Moving'
                      : selectedMarker.pos?.s === 0
                        ? 'Stopped'
                        : 'No GPS'}
                    {' · '}
                    {selectedMarker.pos?.s != null ? `${selectedMarker.pos.s} km/h` : '—'}
                  </div>
                  {selectedMarker.pos?.t != null && (
                    <div className='vm-infowindow__time'>
                      {moment.unix(selectedMarker.pos.t).format('YYYY-MM-DD HH:mm:ss')}
                      <span className='ms-1'>
                        ({moment.unix(selectedMarker.pos.t).fromNow()})
                      </span>
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}

        {showMapToolbar && (
          <div className='vm-map-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={!hasVisibleMarkers}
              onClick={() => {
                if (!map || !isGoogleLoaded) return
                const valid = markers.filter((item: any) => item?.pos?.x && item?.pos?.y)
                if (!valid.length) return
                const bounds = new google.maps.LatLngBounds()
                valid.forEach((item: any) => bounds.extend({lat: item.pos.y, lng: item.pos.x}))
                map.fitBounds(bounds, 64)
              }}
            >
              Fit all
            </button>
            <button
              type='button'
              className={`btn btn-sm ${followVehicleId ? 'btn-primary' : 'btn-light'}`}
              disabled={!selectedMarkerId && !selectedFocusId && !followVehicleId}
              onClick={onToggleFollow}
            >
              {followVehicleId ? 'Following' : 'Follow'}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={onClearSelection}
              disabled={!selectedMarkerId && !selectedFocusId && !followVehicleId}
            >
              Clear
            </button>
          </div>
        )}

        {showLegend && (
          <div className='vm-map-legend'>
            <div className='vm-map-legend__item'>
              <span className='vm-map-legend__swatch vm-map-legend__swatch--moving' />
              Moving
            </div>
            <div className='vm-map-legend__item'>
              <span className='vm-map-legend__swatch vm-map-legend__swatch--stopped' />
              Stopped
            </div>
            <div className='vm-map-legend__item'>
              <span className='vm-map-legend__swatch vm-map-legend__swatch--no-gps' />
              No GPS
            </div>
          </div>
        )}

        {showLegend && MapViewVehicle?.length === 0 && (
          <div className='vm-map-empty-hint'>
            <span>Select vehicles from the list to show on map</span>
            {onShowAll ? (
              <button type='button' className='btn btn-sm btn-primary ms-2' onClick={onShowAll}>
                Show all
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export {MapComponent}
